import {
  collection,
  CollectionReference,
  deleteDoc,
  doc,
  DocumentData,
  DocumentReference,
  Firestore,
  getDoc,
  getDocs,
  orderBy,
  query,
  Query,
  QueryOrderByConstraint,
  setDoc,
  Timestamp,
  where,
  writeBatch,
} from 'firebase/firestore';

import { FieldPath, WhereFilterOp } from 'firebase/firestore';

export type WhereArgs = [fieldPath: string | FieldPath, opStr: WhereFilterOp, value: unknown];

export interface QueryObject {
  where?: WhereArgs[];
  orderBy?: [string, 'asc' | 'desc'][];
}
export type QueryArgs = WhereArgs[] | [string] | [string, ...WhereArgs[]] | [QueryObject];


class FireCRUD<T = Record<string, unknown>> {
  private collectionRef: CollectionReference;

  constructor(private collectionName: string, private db: Firestore | CollectionReference) {
    this.collectionRef = collection(db as Firestore, this.collectionName);
  }

  subCollection(name: string) {
    return new FireCRUD(name, this.collectionRef);
  }

  query(...args: QueryArgs) {
    let queryObj: Query | DocumentReference;
    const [arg1, ...restArgs] = args;

    if (typeof arg1 === 'string') {
      const id = arg1;

      queryObj = doc(this.collectionRef, id);

      if (restArgs.length) {
        const constraints = (restArgs as WhereArgs[]).map((arg) => where(...arg));
        queryObj = query(this.collectionRef, ...constraints);
      }
    } else if ((args as WhereArgs[]).some((arg) => Array.isArray(arg) && arg.every(Boolean))) {
      const constraints = (args as WhereArgs[]).map((arg) => where(...arg));
      queryObj = query(this.collectionRef, ...constraints);
    } else if(typeof args[0] === 'object') {
      const {where: whereArgs, orderBy: orderByArgs} = args[0] as QueryObject;

      const whereConstraints = whereArgs?.every(Boolean) ? whereArgs.map((arg) => where(...arg)) : [];
      const orderByConstraints = orderByArgs?.every(Boolean) ? orderByArgs.map((field) => orderBy(...field)) : [];

      queryObj = query(this.collectionRef, ...whereConstraints, ...orderByConstraints);
    } else {
      queryObj = query(this.collectionRef);
    }

    return queryObj;
  }

  getAll() {
    return getDocs(this.collectionRef).then((querySnap) => {
      return querySnap.docs.map((doc) => doc.data());
    });
  }

  async updateAll( data: Partial<T>, ...args: WhereArgs[]): Promise<void> {
    const constraints = (args as WhereArgs[]).map((arg) => where(...arg));
    const queryObj = query(this.collectionRef, ...constraints);

    const querySnapshot = await getDocs(queryObj);
    if (!querySnapshot.empty && this.db) {
      const batch = writeBatch(this.db as Firestore);
      querySnapshot.forEach((doc) => {
        batch.delete(doc.ref);
      });
      await batch.commit();
    }
  }

  async create(data: Omit<T, 'id' | 'createdAt' | 'updatedAt'>, ...pathSegments: string[]): Promise<T> {
    const docRef = doc(this.collectionRef, ...pathSegments);
    const model = { ...data, id: docRef.id, createdAt: Timestamp.now() } as T;
    await setDoc(docRef, model as DocumentData);
    return model;
  }

  async read(id: string | WhereArgs[]): Promise<T | T[] | undefined> {
    let data: T | T[] | undefined;

    if (typeof id === 'string') {
      const docRef = doc(this.collectionRef, id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        data = docSnap.data() as T;
      }
    } else if (Array.isArray(id)) {
      const args = id as WhereArgs;
      const constraints = (args as WhereArgs[]).map((arg) => where(...arg));
      const queryObj = query(this.collectionRef, ...constraints);
      const querySnap = await getDocs(queryObj);
      if (querySnap.empty) return;
      data = querySnap.docs.map((item) => item.data()) as T[];

      if (data.length === 1) {
        data = data[0];
      }
    } else {
      const querySnapshot = await getDocs(this.collectionRef);
      if (!querySnapshot.empty) {
        data = querySnapshot.docs.map((doc) => doc.data()) as T[];
      }
    }

    return data;
  }

  async update(id: string | string[], data: Partial<T>): Promise<void> {
    let docRef: DocumentReference;
    if (Array.isArray(id)) {
      docRef = doc(this.collectionRef, ...id);
    } else {
      docRef = doc(this.collectionRef, id);
    }
    await setDoc(docRef, { ...data, updatedAt: Timestamp.now() }, { merge: true });
  }

  async delete(id: string | string[]): Promise<void> {
    let docRef: DocumentReference;
    if (Array.isArray(id)) {
      docRef = doc(this.collectionRef, ...id);
    } else {
      docRef = doc(this.collectionRef, id);
    }
    await deleteDoc(docRef);
  }

  async deleteAll(...filters: QueryArgs): Promise<void> {
    let queryOb: Query | DocumentReference;
    if (typeof filters[0] === 'string') {
      const [id] = filters;
      queryOb = collection(this.collectionRef, id);
    } else {
      const constraints = (filters as WhereArgs[]).map((filter) => where(...filter));
      queryOb = query(this.collectionRef, ...constraints);
    }

    const querySnapshot = await getDocs(queryOb);

    if (!this.db) {
      console.error('db is not available');
    }

    if (!querySnapshot.empty && this.db) {
      const batch = writeBatch(this.db);
      querySnapshot.forEach((doc) => {
        batch.delete(doc.ref);
      });
      await batch.commit();
    }
  }
}

export default FireCRUD;
